import React, { useEffect, useMemo, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { useLocation, useNavigate } from "react-router-dom";
import Footer from "../components/Footer";
import SeoBreadcrumb from "../components/SeoBreadcrumb";
import SeoSearchCTA from "../components/seo/SeoSearchCTA";

const API = process.env.REACT_APP_BACKEND_URL;
const SEO_CSS_HREF = `${API}/api/seo/static/seo.css`;
const SEO_JS_HREF = `${API}/api/seo/static/flight-search.js`;
const FONTS_HREF =
  "https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Outfit:wght@600;700;800&display=swap";

// Module-level cache so back/forward navigation is instant.
const fragmentCache = new Map();

function ensureAsset(tag, attrs, id) {
  if (typeof document === "undefined") return null;
  let el = document.getElementById(id);
  if (el) return el;
  el = document.createElement(tag);
  el.id = id;
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  document.head.appendChild(el);
  return el;
}

function setMetaTag(name, content, isProperty = false) {
  if (!content || typeof document === "undefined") return null;
  const attr = isProperty ? "property" : "name";
  let el = document.head.querySelector(`meta[${attr}="${name}"][data-seo-meta]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    el.setAttribute("data-seo-meta", "1");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
}

function setCanonical(href) {
  if (!href || typeof document === "undefined") return;
  let el = document.head.querySelector('link[rel="canonical"][data-seo-meta]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    el.setAttribute("data-seo-meta", "1");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setSchemaLD(jsonString) {
  if (typeof document === "undefined") return;
  const existing = document.head.querySelectorAll(
    'script[type="application/ld+json"][data-seo-meta]'
  );
  existing.forEach((e) => e.remove());
  if (!jsonString) return;
  const s = document.createElement("script");
  s.type = "application/ld+json";
  s.setAttribute("data-seo-meta", "1");
  s.text = jsonString;
  document.head.appendChild(s);
}

function clearSeoMeta() {
  if (typeof document === "undefined") return;
  document
    .querySelectorAll("[data-seo-meta]")
    .forEach((e) => e.remove());
}

function fireAnalytics(path, pageType) {
  try {
    let sid = sessionStorage.getItem("acf_sid");
    if (!sid) {
      sid =
        "seo_" +
        Math.random().toString(36).slice(2) +
        Date.now().toString(36);
      sessionStorage.setItem("acf_sid", sid);
    }
    const payload = JSON.stringify({
      events: [
        {
          session_id: sid,
          event_type: "pageview",
          path,
          data: { source: "seo_ssr_react", page_type: pageType },
        },
      ],
    });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(
        `${API}/api/analytics/track`,
        new Blob([payload], { type: "application/json" })
      );
    } else {
      fetch(`${API}/api/analytics/track`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload,
        keepalive: true,
      });
    }
  } catch (_) {
    /* non-blocking */
  }
}

/**
 * SeoPageRenderer — fast, fully responsive in-app renderer for the public
 * SEO pages (route / airport / airline / country / destination / visa
 * + their master hubs).
 *
 * Fetches a JSON fragment from /api/seo/render-fragment and injects the
 * HTML inline so the page lives inside the React shell (single header,
 * same navigation, no iframe, no double-scroll, no double-load).
 */
export default function SeoPageRenderer() {
  const location = useLocation();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [status, setStatus] = useState("loading");
  const [fragment, setFragment] = useState(null);

  const path = location.pathname;
  const cacheKey = useMemo(() => path, [path]);

  // Lazy-load the SEO CSS + fonts once. Removed on unmount.
  useEffect(() => {
    const cssEl = ensureAsset(
      "link",
      { rel: "stylesheet", href: SEO_CSS_HREF },
      "seo-css"
    );
    const fontEl = ensureAsset(
      "link",
      { rel: "stylesheet", href: FONTS_HREF },
      "seo-fonts"
    );
    return () => {
      if (cssEl && cssEl.parentNode) cssEl.parentNode.removeChild(cssEl);
      if (fontEl && fontEl.parentNode) fontEl.parentNode.removeChild(fontEl);
      clearSeoMeta();
    };
  }, []);

  // Fetch fragment on path change.
  useEffect(() => {
    let cancelled = false;

    const apply = (data) => {
      if (cancelled) return;
      setFragment(data);
      setStatus("ready");
      // Update browser tab title, OG/canonical/schema for SEO crawlers
      if (data.title) document.title = data.title;
      setMetaTag("description", data.description);
      setMetaTag("og:title", data.title, true);
      setMetaTag("og:description", data.description, true);
      setMetaTag("og:type", "website", true);
      setMetaTag("og:url", data.canonical_url, true);
      setMetaTag("og:site_name", "ACF Tourism", true);
      const ogImage = data.canonical_url
        ? `${new URL(data.canonical_url).origin}/acf-tourism-og.jpg`
        : `${window.location.origin}/acf-tourism-og.jpg`;
      setMetaTag("og:image", ogImage, true);
      setMetaTag("twitter:card", "summary_large_image");
      setMetaTag("twitter:title", data.title);
      setMetaTag("twitter:description", data.description);
      setMetaTag("twitter:image", ogImage);
      setCanonical(data.canonical_url);
      setSchemaLD(data.schema_json);
      fireAnalytics(path, data.page_type);
    };

    if (fragmentCache.has(cacheKey)) {
      apply(fragmentCache.get(cacheKey));
      return () => {
        cancelled = true;
      };
    }

    setStatus("loading");
    fetch(
      `${API}/api/seo/render-fragment?path=${encodeURIComponent(path)}`
    )
      .then(async (r) => {
        if (r.status === 404) {
          throw new Error("notfound");
        }
        if (!r.ok) {
          throw new Error("error");
        }
        return r.json();
      })
      .then((data) => {
        fragmentCache.set(cacheKey, data);
        apply(data);
      })
      .catch((err) => {
        if (cancelled) return;
        setStatus(err.message === "notfound" ? "notfound" : "error");
      });

    return () => {
      cancelled = true;
    };
  }, [path, cacheKey]);

  // Lazy-load the flight-search.js for the hub when needed.
  useEffect(() => {
    if (status !== "ready" || !fragment?.include_flight_search) return;
    const id = "seo-flight-search-js";
    if (document.getElementById(id)) return;
    const s = document.createElement("script");
    s.id = id;
    s.src = SEO_JS_HREF;
    s.defer = true;
    document.body.appendChild(s);
    return () => {
      const el = document.getElementById(id);
      if (el && el.parentNode) el.parentNode.removeChild(el);
    };
  }, [status, fragment]);

  // Intercept internal anchor clicks → React Router navigate.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = (ev) => {
      const a = ev.target.closest && ev.target.closest("a");
      if (!a) return;
      const href = a.getAttribute("href") || "";
      if (
        href.startsWith("/") &&
        !href.startsWith("/api/") &&
        !href.startsWith("/static/") &&
        a.target !== "_blank"
      ) {
        ev.preventDefault();
        navigate(href);
      }
    };
    el.addEventListener("click", handler);
    return () => el.removeEventListener("click", handler);
  }, [navigate, fragment]);

  // Pull search-prefill from the fragment when present (route/airport/destination).
  const heroDefaults = useMemo(() => {
    const d = fragment?.flight_search_defaults;
    if (!d) return null;
    return { from: d.origin_iata || null, to: d.dest_iata || null };
  }, [fragment]);

  if (status === "loading") {
    return (
      <div data-testid="seo-page-loading" style={loadingStyle}>
        <div className="seo-loading-spinner" aria-hidden="true" />
        <p style={{ color: "#64748b", marginTop: 12 }}>Loading…</p>
      </div>
    );
  }

  if (status === "notfound") {
    return (
      <div data-testid="seo-page-notfound" style={messageStyle}>
        <h1 style={{ fontSize: 32, marginBottom: 12 }}>Page not found</h1>
        <p style={{ color: "#475569", marginBottom: 18 }}>
          The travel page you're looking for isn't available right now.
        </p>
        <a
          href="/"
          onClick={(e) => {
            e.preventDefault();
            navigate("/");
          }}
          style={{ color: "#1e40af", fontWeight: 600, textDecoration: "none" }}
        >
          ← Back to home
        </a>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div data-testid="seo-page-error" style={messageStyle}>
        <h2>Something went wrong</h2>
        <p style={{ color: "#475569" }}>
          We couldn't load this page. Please try again shortly.
        </p>
      </div>
    );
  }

  // Route, airport, destination pages and route hub all benefit from a clear
  // "Search Flights" CTA above the breadcrumb. Per the latest UX directive
  // we no longer embed the homepage form here — the CTA hands off to the
  // homepage so the rich search UI lives in exactly one place.
  const showFlightSearch = fragment?.include_flight_search === true;

  return (
    <div className="seo-page-wrap">
      {showFlightSearch && (
        <SeoSearchCTA prefill={heroDefaults} />
      )}
      <SeoBreadcrumb items={fragment?.breadcrumbs} />
      <div
        ref={containerRef}
        data-testid="seo-page-renderer"
        data-seo-page-type={fragment?.page_type || ""}
        data-seo-slug={fragment?.slug || ""}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(fragment?.body_html || "", {
            USE_PROFILES: { html: true },
          }),
        }}
      />
      <Footer />
    </div>
  );
}

const loadingStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "60vh",
  padding: "60px 20px",
};

const messageStyle = {
  maxWidth: 720,
  margin: "0 auto",
  padding: "80px 20px",
  textAlign: "center",
  minHeight: "60vh",
};
